import type { SuggestionDto } from '~/composables/useSearchAutocompleteAndHistory'
import { useSearchAutocompleteAndHistory } from '~/composables/useSearchAutocompleteAndHistory'
import type { SuggestionType } from '~/types'

function isIgnorableHistoryError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const name = 'name' in err ? String(err.name) : ''
  const message = 'message' in err ? String(err.message) : ''
  return name === 'AbortError' || message.includes('aborted') || message.includes('Missing customer handle')
}

export const useSearchStore = defineStore('search', () => {
  const api = useSearchAutocompleteAndHistory()

  const data = ref<SuggestionDto>({ suggestions: [] })
  const history = ref<SuggestionDto>({ suggestions: [] })
  const query = ref<string | null>(null)
  const isLoading = ref(false)

  const searchTerm = ref<string | null>(null)
  type SearchOrigin = 'typed' | 'atc_code_click' | `suggestion-${SuggestionType}`
  const searchOrigin = ref<SearchOrigin | null>(null)

  function setSearchMeta(term: string, origin: SearchOrigin) {
    searchTerm.value = term
    searchOrigin.value = origin
  }

  function clearSearchMeta() {
    searchTerm.value = null
    searchOrigin.value = null
  }

  function resetToHistory() {
    data.value.suggestions = history.value.suggestions
    // History is not autocomplete for a typed query; clear so suggestion gates
    // don't treat stale history as a match for the previous search string.
    query.value = null
  }

  async function getSearchHistory() {
    const { $i18n, $storeErrorLog } = useNuxtApp()
    isLoading.value = true

    try {
      const responseData = await api.getSearchHistory()
      // Clone so autocomplete mutating `data.suggestions` cannot overwrite history.
      data.value = { suggestions: [...responseData.suggestions] }
      history.value = { suggestions: [...responseData.suggestions] }
      query.value = null
    }
    catch (err: unknown) {
      if (isIgnorableHistoryError(err)) return
      $storeErrorLog('stores/search.ts', 'Could not load search history', $i18n.t('errors.page.unknown.subtitle'), err)
    }

    isLoading.value = false
  }

  async function autocomplete(searchQuery: string) {
    const { $i18n, $storeErrorLog } = useNuxtApp()

    isLoading.value = true
    try {
      const responseData = await api.autocomplete(searchQuery)
      data.value.suggestions = responseData.suggestions
      query.value = searchQuery
    }
    catch (err: unknown) {
      let snackbarMessage = $i18n.t('errors.page.unknown.subtitle')
      if (err && typeof err === 'object' && 'data' in err) {
        const errorData = err as { data?: { data?: string } }
        if (errorData.data?.data)
          snackbarMessage = errorData.data.data
      }
      $storeErrorLog('stores/search.ts', `Could not load autocomplete by query: ${searchQuery}`, snackbarMessage, err)
    }

    isLoading.value = false
  }

  function reset() {
    data.value = { suggestions: [] }
    history.value = { suggestions: [] }
    query.value = null
    isLoading.value = false
  }

  return {
    data,
    history,
    query,
    isLoading,
    searchTerm,
    searchOrigin,
    resetToHistory,
    getSearchHistory,
    autocomplete,
    reset,
    setSearchMeta,
    clearSearchMeta,
  }
})
